import React, { createContext, useCallback, useContext, useState } from 'react';

import { LockConfirmationModal } from './LockConfirmationModal';
import type { LockConfirmationModalProps } from './LockConfirmationModal';

/**
 * DistributiveOmit keeps `LockConfirmationModalProps` a discriminated union
 * after we strip the modal-controlled props. Without `T extends any ? ... : never`
 * TS collapses the union into a flat shape and we lose the per-target
 * required fields (e.g. `count` / `scope` on `target: 'rows'`).
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

type LockConfirmRequest = DistributiveOmit<
  LockConfirmationModalProps,
  'open' | 'onCancel' | 'onConfirm'
> & {
  onConfirm: () => void | Promise<void>;
};

interface LockConfirmationContextValue {
  requestLockConfirmation: (request: LockConfirmRequest) => void;
}

/**
 * Default is a no-op so consumers outside a provider (e.g. the question
 * editor menu rendered in a `document` context) can still call the hook
 * unconditionally. Lock buttons in those contexts only render when a
 * report column lock state is available, so the no-op never fires.
 */
const LockConfirmationContext = createContext<LockConfirmationContextValue>({
  requestLockConfirmation: () => {},
});

/**
 * Renders a single `LockConfirmationModal` at a stable level in the tree so
 * triggers (column header menu items, row toolbar buttons, cell popover) can
 * close themselves on click without unmounting the modal.
 */
export function LockConfirmationProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [request, setRequest] = useState<LockConfirmRequest | null>(null);

  const requestLockConfirmation = useCallback((next: LockConfirmRequest) => {
    setRequest(next);
  }, []);

  const handleCancel = useCallback(() => setRequest(null), []);

  const handleConfirm = useCallback(async () => {
    if (!request) return;
    try {
      await request.onConfirm();
    } finally {
      setRequest(null);
    }
  }, [request]);

  return (
    <LockConfirmationContext.Provider value={{ requestLockConfirmation }}>
      {children}
      {request && renderModal(request, { onCancel: handleCancel, onConfirm: handleConfirm })}
    </LockConfirmationContext.Provider>
  );
}

interface ModalCallbacks {
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

function renderModal(request: LockConfirmRequest, callbacks: ModalCallbacks): JSX.Element {
  const { onCancel, onConfirm } = callbacks;
  switch (request.target) {
    case 'cell':
      return (
        <LockConfirmationModal
          open
          action={request.action}
          target="cell"
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      );
    case 'column':
      return (
        <LockConfirmationModal
          open
          action={request.action}
          target="column"
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      );
    case 'rows':
      if (request.scope === 'all') {
        return (
          <LockConfirmationModal
            open
            action={request.action}
            scope="all"
            target="rows"
            onCancel={onCancel}
            onConfirm={onConfirm}
          />
        );
      }
      return (
        <LockConfirmationModal
          open
          action={request.action}
          count={request.count}
          scope="selection"
          target="rows"
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      );
  }
}

export function useLockConfirmation(): LockConfirmationContextValue {
  return useContext(LockConfirmationContext);
}
